import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-03c0e5c8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "field-wrapper" }
const _hoisted_2 = { class: "datebox-container" }

import { FieldSize } from "@/enums/system/FieldSize";
import { PropType, computed, ref, watch } from "vue";
import { DxDateBox } from "devextreme-vue/date-box";
import { ValidatedEvent } from "devextreme/ui/validator";
import { DxValidator } from "devextreme-vue";
import { DxCustomRule, DxRequiredRule } from "devextreme-vue/validator";
import { formatCurrentDate } from "bright-logic-core/src/format";
import moment from "moment";


export default /*#__PURE__*/_defineComponent({
  __name: 'field-validation-free-date-box',
  props: {
  /**
   * The string value used with `v-model` to control the visibility or state of a modal or component.
   * @type {string}
   * @required
   */
  modelValue: {
    type: String,
    required: false,
    default: null,
  },
  /**
   * Determines whether the specific control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The label displayed for the control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * The width  for the control.
   * @type String as PropType<FieldSize>
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * The max date for the control.
   * @type Date
   * @default new Date(new Date().setFullYear(new Date().getFullYear() + 50))
   */
  maxDate: {
    type: Date,
    default: new Date(new Date().setFullYear(new Date().getFullYear() + 50)),
  },
  /**
   * The min date for the control.
   * @type Date
   * @default new Date('1900-01-01')
   */
  minDate: {
    type: Date,
    default: new Date("1900-01-01"),
  },
  /**
   * Determines whether the specific control is disabled.
   * @type {boolean}
   * @default false
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the time is included.
   * @type {boolean}
   * @default false
   */
  includeTime: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the specific control is required.
   * @type {boolean}
   * @default false
   */
  required: {
    type: Boolean,
    default: false,
  },
  /**
   * The validationGroup which the control belongs to.
   * @type String
   * @default 'false'
   */
  validationGroup: {
    type: String,
    default: "false",
  },
},
  emits: [
  /**
   * Emitted when the internal date value changes, passing a formatted string (ISO or custom).
   * Typically used with v-model.
   * @event update:modelValue
   * @type {string}
   */
  "update:modelValue",
  /**
   * Emitted when the user selects or clears a date via the UI (e.g., onValueChanged).
   * Useful for listening to raw DevExtreme change events.
   * @event valueSelected
   * @type {object}
   */
  "valueSelected",
],
  setup(__props, { emit }) {

const props = __props;





const dateValue = computed<string | null>({
  get: () => (props.modelValue == "" ? null : props.modelValue),
  set: (value: string | null) =>
    emit("update:modelValue", value == "" ? null : value),
});

/** Emits the value changed DxDateBox event to allow parent component to perform custom logic */
const onValueChanged = (e: any) => {
  emit("valueSelected", String(e));
};

/** Custom validation configuration to override DevExpress default validation method */
const adapterConfig = {
  getValue: () => {
    return dateValue.value;
  },
  applyValidationResults: (e: ValidatedEvent) => {
    isValid.value = !!e.isValid;

    // Construct list of validation errors to be displayed to the user when they hover over the field
    validationErrors.value =
      !e.isValid && e.brokenRule?.message ? e.brokenRule.message : "";
  },
  validationRequestsCallbacks: [],
};

const validationErrors = ref("");
const isValid = ref(true);

/** Handles custom max date validation */
const maxDateValidation = () => {
  // Allow undefined/null dates
  if (!dateValue.value) {
    return true;
  }

  moment.locale("en-GB");
  return props.maxDate
    ? moment(dateValue.value).isSameOrBefore(moment(props.maxDate))
    : true;
};

/** Handles custom min date validation */
const minDateValidation = () => {
  // Allow undefined/null dates
  if (!dateValue.value) {
    return props.minDate.toUTCString() === new Date("1900-01-01").toUTCString();
  }

  moment.locale("en-GB");
  return props.minDate
    ? moment(dateValue.value).isSameOrAfter(moment(props.minDate))
    : true;
};

const dateBoxType = computed(() => (props.includeTime ? "datetime" : "date"));
const displayFormat = computed(() =>
  props.includeTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy",
);

const dateTimeDropDownOptions = {
  onHiding: () => {
    let button: HTMLElement = document.querySelector(
      ".dx-popup-done.dx-button",
    ) as HTMLElement;
    if (!button) {
      return;
    }
    button.click();
  },
};

const dateOnlyDropDownOptions = {
  toolbarItems: [
    {
      widget: "dxButton",
      toolbar: "bottom",
      location: "before",
      options: {
        text: "Today",
        onClick: () => onTodayClick(),
        disabled: props.maxDate < new Date(formatCurrentDate()),
      },
    },
  ],
};

const dropDownOptions = computed(() =>
  props.includeTime ? dateTimeDropDownOptions : dateOnlyDropDownOptions,
);

const onTodayClick = () => {
  dateValue.value = formatCurrentDate();
  opened.value = false;
};

const opened = ref(false);

const maxValidationMessage = computed(() => {
  return props.maxDate.setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0)
    ? "The date must be today's date or earlier"
    : `The date must be ${props.maxDate.toLocaleDateString(
        "en-GB",
      )} or earlier`;
});

const minValidationMessage = computed(() => {
  return `The date must be ${props.minDate.toLocaleDateString(
    "en-GB",
  )} or later`;
});

watch(
  () => dateValue.value,
  () => {
    if (isValid.value) {
      return;
    }

    if (dateValue.value) {
      if (new Date(dateValue.value) > props.maxDate) {
        validationErrors.value = maxValidationMessage.value;
      } else if (new Date(dateValue.value) < props.minDate) {
        validationErrors.value = minValidationMessage.value;
      }
    }
  },
);

const requiredMessage = computed(() =>
  props.label ? `${props.label} is required` : "This field is required",
);

// To overcome focus issues on iOS devices, the date box should be set to read only whilst the calendar is displayed
const dateBoxReadonly = computed(() => props.readOnly || opened.value);

const onOpened = (e: any) => {
  if (screen.width > 1300) {
    return;
  }

  // To stop the top of the calendar being cut off on small devices, reposition it to the center of the screen
  e.component._popup.option("position", {
    my: "center",
    at: "center",
    of: window,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["label", { asteriskVisible: props.required }])
        }, _toDisplayString(__props.label), 3))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(DxDateBox), {
        "display-format": displayFormat.value,
        value: dateValue.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((dateValue).value = $event)),
        width: __props.width,
        readOnly: dateBoxReadonly.value,
        hint: validationErrors.value,
        min: __props.minDate,
        max: __props.maxDate,
        onValueChanged: onValueChanged,
        isValid: isValid.value,
        "onUpdate:isValid": _cache[1] || (_cache[1] = ($event: any) => ((isValid).value = $event)),
        disabled: __props.disabled,
        "date-out-of-range-message": "",
        "use-mask-behavior": true,
        type: dateBoxType.value,
        dropDownOptions: dropDownOptions.value,
        "picker-type": "calendar",
        "validation-message-mode": "auto",
        opened: opened.value,
        "onUpdate:opened": _cache[2] || (_cache[2] = ($event: any) => ((opened).value = $event)),
        onOpened: onOpened,
        dateSerializationFormat: "yyy-MM-dd",
        placeholder: "DD/MM/YYYY"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DxValidator), {
            adapter: adapterConfig,
            "validation-group": props.validationGroup
          }, {
            default: _withCtx(() => [
              (props.required)
                ? (_openBlock(), _createBlock(_unref(DxRequiredRule), {
                    key: 0,
                    message: requiredMessage.value
                  }, null, 8, ["message"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(DxCustomRule), {
                reevaluate: true,
                validationCallback: minDateValidation,
                message: minValidationMessage.value
              }, null, 8, ["message"]),
              _createVNode(_unref(DxCustomRule), {
                reevaluate: true,
                validationCallback: maxDateValidation,
                message: maxValidationMessage.value
              }, null, 8, ["message"])
            ]),
            _: 1
          }, 8, ["validation-group"])
        ]),
        _: 1
      }, 8, ["display-format", "value", "width", "readOnly", "hint", "min", "max", "isValid", "disabled", "type", "dropDownOptions", "opened"])
    ])
  ]))
}
}

})